import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import DisplayImage from '../display-image';
import {maxWidth, NARROW_LAYOUT_MAX} from '@/styles/breakpoints';
import {borderRadius, colors, fontSize, fontWeight, letterSpacing, lineHeight, overlay, shadows} from '@/styles/tokens';

export interface SuccessStory {
    client: string;
    /** Industry pill over the image. */
    tag: string;
    title: string;
    image: string | StaticImageData;
    stats: {value: string; label: string}[];
    /** Unique per card — the study's page path, or its portfolio `id` when it has no page yet. */
    key: string;
    /** Omitted for studies with no page yet; the card then offers the enquiry form instead. */
    href?: string;
}

interface IosSuccessStoriesProps {
    heading: string;
    subheading?: string;
    stories: SuccessStory[];
    /** Opens the enquiry form from cards that have no case-study page. */
    onEnquire?: () => void;
    ctaText?: string;
    onCtaClick?: () => void;
    tone?: 'dark' | 'light';
}

// Cards per view on desktop; also the server-rendered page count before the track is measured.
const DESKTOP_PER_VIEW = 3;
const GAP_PX = 24;
// Quiet time after the last scroll event before the loop copies are swapped for the originals.
const SETTLE_MS = 120;

const trackSx = {
    display: 'flex',
    // Phones show one card at a time, so each keeps its own height instead of stretching
    // to the tallest card in the row; from `sm` up the cards sit side by side and stay level.
    alignItems: {xs: 'flex-start', sm: 'stretch'},
    gap: `${GAP_PX}px`,
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    // Room for the cards' shadow, which `overflow` would otherwise clip.
    py: 1,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {display: 'none'}
} as const;

// Phones show one card with the next peeking in, so it is clear the row scrolls.
const cardSx = {
    position: 'relative',
    flex: {
        xs: '0 0 88%',
        sm: `0 0 calc((100% - ${GAP_PX}px) / 2)`,
        md: `0 0 calc((100% - ${GAP_PX * (DESKTOP_PER_VIEW - 1)}px) / ${DESKTOP_PER_VIEW})`
    },
    scrollSnapAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: borderRadius['3xl'],
    overflow: 'hidden',
    bgcolor: colors.background.default,
    boxShadow: shadows.card,
    // Brand blue running into orange along the card's top edge.
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        zIndex: 1,
        background: `linear-gradient(90deg, ${colors.primary.link} 0%, ${colors.accent.orange} 100%)`
    }
} as const;

const imageWrapSx = {position: 'relative', height: {xs: 200, md: 220}, flexShrink: 0} as const;

// Anchored to the top: the artwork's app headers and faces sit there, so any crop the
// fixed-height frame forces comes off the bottom instead.
const imageStyle: React.CSSProperties = {objectFit: 'cover', objectPosition: 'center top'};

const tagSx = {
    position: 'absolute',
    left: 16,
    bottom: 16,
    px: 1.5,
    py: 0.75,
    borderRadius: borderRadius.full,
    bgcolor: colors.background.default,
    color: colors.primary.deep,
    fontSize: fontSize[11],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase',
    boxShadow: shadows.card
} as const;

const bodySx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    px: {xs: 2.5, md: 3},
    pt: {xs: 2, md: 2.5},
    pb: {xs: 2, md: 2.25}
} as const;

const clientSx = {color: colors.text.orangeAccessible, fontSize: fontSize[13], fontWeight: fontWeight.bold, mb: 1} as const;

// Laptop-class screens (≤1499px) step the card title and stat figures down to 16px and the
// stat labels to 11px — the same breakpoint the section headings use — so three cards per row
// don't feel cramped.
const LAPTOP = maxWidth(NARROW_LAYOUT_MAX);
const LAPTOP_TEXT = {[LAPTOP]: {fontSize: fontSize[16], lineHeight: lineHeight[24]}} as const;

const titleSx = {
    color: colors.text.primary,
    fontSize: fontSize[20],
    lineHeight: lineHeight[28],
    fontWeight: fontWeight.bold,
    mb: 2,
    ...LAPTOP_TEXT
} as const;

// `mt: auto` pins the stats and link to the card's foot, so they line up across a row
// whichever card has the longest title.
const statsSx = {display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mt: 'auto'} as const;

const statTileSx = {
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    px: 1.5,
    py: 1.25,
    minWidth: 0
} as const;

const statValueSx = {
    color: colors.primary.main,
    fontSize: fontSize[20],
    lineHeight: lineHeight[28],
    fontWeight: fontWeight.bold,
    overflowWrap: 'anywhere',
    ...LAPTOP_TEXT
} as const;

// Two lines reserved, so a one-line label's tile matches its neighbours' height and the
// tiles sit level across a row.
const STAT_LABEL_LINE_HEIGHT = 1.4;
const statLabelSx = {
    color: colors.text.secondary,
    fontSize: fontSize[12],
    lineHeight: STAT_LABEL_LINE_HEIGHT,
    minHeight: `${STAT_LABEL_LINE_HEIGHT * 2}em`,
    [LAPTOP]: {fontSize: fontSize[11]}
} as const;

const footSx = {borderTop: `1px solid ${colors.neutral.borderLight}`, mt: 2, pt: 1.75} as const;

const linkSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    color: colors.primary.main,
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    textDecoration: 'none',
    '& .story-arrow': {color: colors.accent.orange, transition: 'transform 0.3s ease'},
    '&:hover .story-arrow': {transform: 'translateX(4px)'},
    '&:focus-visible': {outline: `2px solid ${colors.primary.link}`, outlineOffset: 4, borderRadius: borderRadius.sm}
} as const;

// The unlinked card's enquiry trigger is a native button styled to match the link.
const enquireButtonSx = {...linkSx, p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', fontFamily: 'inherit'} as const;

// Visually hidden: gives each "View case study" link a distinct accessible name.
const srOnlySx = {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap'
} as const;

const controlsRowSx = {display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: {xs: 3, md: 4}} as const;

const pagerSx = {display: 'flex', alignItems: 'center', gap: 1} as const;

const dotSx = (active: boolean, inactive: string) =>
    ({
        width: active ? 28 : 8,
        height: 8,
        p: 0,
        border: 'none',
        cursor: 'pointer',
        borderRadius: borderRadius.full,
        bgcolor: active ? colors.accent.orange : inactive,
        transition: 'width 0.3s ease, background-color 0.3s ease'
    } as const);

const counterSx = {color: colors.text.inverse, fontSize: fontSize[13], fontWeight: fontWeight.bold, ml: 1.5} as const;

// The section sits on the page's deep-blue band (`bg-deep-blue`); the cards stay white.
const sectionTextSx = {color: colors.text.inverse} as const;

// `tone`: `dark` (default) on the deep-blue band; `light` = dark text + muted-grey dots (≥3:1) for pale bands.
const lightTextSx = {color: colors.text.primary} as const;
const TONE_SX = {
    dark: {heading: sectionTextSx, subheading: sectionTextSx, counter: counterSx, dot: overlay.whiteBorder},
    light: {heading: lightTextSx, subheading: lightTextSx, counter: {...counterSx, color: colors.text.secondary}, dot: colors.text.muted}
} as const;

// The fixed WhatsApp / chat launchers own the rightmost ~88px of the viewport, so the arrows
// are kept clear of that band. `(100vw - 100%) / 2` is the gap the container already leaves
// on each side; the margin only makes up the difference, and is zero on wide screens.
const arrowsSx = {display: 'flex', gap: 1.5, mr: 'max(0px, calc(88px - (100vw - 100%) / 2))'} as const;

const arrowSx = {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: borderRadius.circle,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.default,
    color: colors.primary.deep,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: shadows.card,
    transition: 'background-color 0.3s ease, color 0.3s ease',
    '&:hover:not(:disabled)': {bgcolor: colors.accent.orange, color: colors.text.inverse},
    '&:disabled': {opacity: 0.4, cursor: 'default'}
} as const;

const arrowIconSx = {fontSize: fontSize[18]} as const;

const ctaWrapSx = {textAlign: 'center', mt: {xs: 3, md: 4}} as const;

const pad = (n: number) => String(n).padStart(2, '0');

// Loop copies are decorative: hidden from assistive tech and unfocusable (`inert`; React 18
// has no typed prop for it, so it goes through as a plain attribute).
const cloneAttrs = {'aria-hidden': true, inert: ''} as Record<string, unknown>;

const StoryCard = ({story, onEnquire, isClone}: {story: SuccessStory; onEnquire?: () => void; isClone?: boolean}) => (
    <Box component="article" sx={cardSx} {...(isClone ? cloneAttrs : {})}>
        <Box sx={imageWrapSx}>
            <DisplayImage
                src={story.image}
                alt={`${story.client} case study`}
                fill
                sizes="(max-width: 600px) 88vw, (max-width: 900px) 50vw, 33vw"
                style={imageStyle}
            />
            <Box component="span" className="font-headers" sx={tagSx}>
                {story.tag}
            </Box>
        </Box>
        <Box sx={bodySx}>
            <Typography component="p" className="font-headers" sx={clientSx}>
                {story.client}
            </Typography>
            <Typography component="h3" className="font-headers" sx={titleSx}>
                {story.title}
            </Typography>
            <Box sx={statsSx}>
                {story.stats.map((stat) => (
                    <Box key={stat.label} sx={statTileSx}>
                        <Typography component="p" className="font-headers" sx={statValueSx}>
                            {stat.value}
                        </Typography>
                        <Typography component="p" className="font-regular-text" sx={statLabelSx}>
                            {stat.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {(story.href || onEnquire) && (
                <Box sx={footSx}>
                    {story.href ? (
                        <Box component={Link} href={story.href} className="font-headers" sx={linkSx}>
                            View case study
                            <Box component="span" sx={srOnlySx}>
                                : {story.client}
                            </Box>
                            <Box component="span" className="story-arrow" aria-hidden="true">
                                →
                            </Box>
                        </Box>
                    ) : (
                        <Box component="button" type="button" onClick={onEnquire} className="font-headers" sx={enquireButtonSx}>
                            Discuss a similar project
                            <Box component="span" sx={srOnlySx}>
                                : {story.client}
                            </Box>
                            <Box component="span" className="story-arrow" aria-hidden="true">
                                →
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    </Box>
);

const IosSuccessStories = ({heading, subheading, stories, onEnquire, ctaText, onCtaClick, tone = 'dark'}: IosSuccessStoriesProps) => {
    const toneSx = TONE_SX[tone];
    const trackRef = useRef<HTMLDivElement>(null);
    // Only loop when there is more than one desktop page; otherwise the copies would make a
    // non-scrolling row scrollable.
    const hasLoop = (stories?.length ?? 0) > DESKTOP_PER_VIEW;
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(Math.max(1, Math.ceil((stories?.length ?? 0) / DESKTOP_PER_VIEW)));

    // One "page" is however many whole cards fit in the track at its current width.
    const measure = useCallback(() => {
        const track = trackRef.current;
        const card = track?.firstElementChild as HTMLElement | null;
        if (!track || !card) return null;
        const advance = card.offsetWidth + GAP_PX;
        const perView = Math.max(1, Math.floor((track.clientWidth + GAP_PX) / advance));
        return {
            step: perView * advance,
            pages: Math.max(1, Math.ceil(stories.length / perView)),
            // Where the loop copies begin — visually identical to page 1.
            cloneStart: stories.length * advance,
            track
        };
    }, [stories.length]);

    const sync = useCallback(() => {
        const m = measure();
        if (!m) return;
        const left = m.track.scrollLeft;
        let current = Math.round(left / m.step);
        if (hasLoop && left >= m.cloneStart - 2) {
            // On the loop copies the view looks like page 1, so it reads as page 1.
            current = 0;
        } else if (!hasLoop && left >= m.track.scrollWidth - m.track.clientWidth - 2) {
            // Without the loop, a partial last page never reaches a full step at the far end.
            current = m.pages - 1;
        }
        setPageCount(m.pages);
        setPage(Math.min(current, m.pages - 1));
    }, [measure, hasLoop]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return undefined;
        // Once scrolling settles on the loop copies, jump back onto the real cards by the same
        // offset. It is instant and the copies match the originals, so the jump is invisible —
        // this is what lets "next" from the last page keep moving right into page 1.
        let settleTimer: ReturnType<typeof setTimeout> | undefined;
        const settle = () => {
            const m = measure();
            if (!m || !hasLoop || m.track.scrollLeft < m.cloneStart - 2) return;
            m.track.scrollTo({left: m.track.scrollLeft - m.cloneStart, behavior: 'auto'});
        };
        const onScroll = () => {
            sync();
            clearTimeout(settleTimer);
            settleTimer = setTimeout(settle, SETTLE_MS);
        };

        sync();
        track.addEventListener('scroll', onScroll, {passive: true});
        const observer = new ResizeObserver(sync);
        observer.observe(track);
        return () => {
            clearTimeout(settleTimer);
            track.removeEventListener('scroll', onScroll);
            observer.disconnect();
        };
    }, [sync, measure, hasLoop]);

    const scrollToLeft = (track: HTMLDivElement, left: number) => {
        const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({left, behavior: reduce ? 'auto' : 'smooth'});
    };

    const goTo = (target: number) => {
        const m = measure();
        if (m) scrollToLeft(m.track, target * m.step);
    };

    // With more than one desktop page the arrows loop in their own direction: next from the
    // last page scrolls right onto the copies of page 1 (then `settle` swaps in the real
    // page 1), and previous from page 1 first jumps, invisibly, onto those copies and then
    // scrolls left into the last page. With fewer stories (no loop copies) the arrows stop
    // at the ends and disable, as before.
    const goNext = () => {
        const m = measure();
        if (!m) return;
        if (page < pageCount - 1) goTo(page + 1);
        else if (hasLoop) scrollToLeft(m.track, m.cloneStart);
    };
    const goPrev = () => {
        const m = measure();
        if (!m) return;
        if (page > 0) {
            goTo(page - 1);
        } else if (hasLoop) {
            m.track.scrollTo({left: m.cloneStart, behavior: 'auto'});
            requestAnimationFrame(() => scrollToLeft(m.track, (pageCount - 1) * m.step));
        }
    };

    if (!stories?.length) return null;

    return (
        <Box>
            <MUIContainer>
                <SectionHeader heading={heading} subheading={subheading} headingSx={toneSx.heading} subheadingSx={toneSx.subheading} />

                <Box ref={trackRef} sx={trackSx}>
                    {stories.map((story) => (
                        <StoryCard key={story.key} story={story} onEnquire={onEnquire} />
                    ))}
                    {hasLoop &&
                        stories
                            .slice(0, DESKTOP_PER_VIEW)
                            .map((story) => <StoryCard key={`${story.key}-loop`} story={story} onEnquire={onEnquire} isClone />)}
                </Box>

                {pageCount > 1 && (
                    <Box sx={controlsRowSx}>
                        <Box sx={pagerSx}>
                            {Array.from({length: pageCount}, (_, i) => (
                                <Box
                                    key={i}
                                    component="button"
                                    type="button"
                                    aria-label={`Show case studies page ${i + 1}`}
                                    aria-current={i === page}
                                    onClick={() => goTo(i)}
                                    sx={dotSx(i === page, toneSx.dot)}
                                />
                            ))}
                            <Typography component="span" className="font-headers" sx={toneSx.counter} aria-live="polite">
                                {pad(page + 1)} / {pad(pageCount)}
                            </Typography>
                        </Box>
                        <Box sx={arrowsSx}>
                            <Box
                                component="button"
                                type="button"
                                aria-label="Previous case studies"
                                disabled={!hasLoop && page === 0}
                                onClick={goPrev}
                                sx={arrowSx}
                            >
                                <ArrowBack sx={arrowIconSx} />
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                aria-label="Next case studies"
                                disabled={!hasLoop && page === pageCount - 1}
                                onClick={goNext}
                                sx={arrowSx}
                            >
                                <ArrowForward sx={arrowIconSx} />
                            </Box>
                        </Box>
                    </Box>
                )}

                {ctaText && onCtaClick && (
                    <Box sx={ctaWrapSx}>
                        <Button
                            variant="contained"
                            disableRipple={true}
                            className="animated-button button-bg-primary"
                            onClick={onCtaClick}
                            endIcon={<ArrowOutward fontSize="small" />}
                        >
                            {ctaText}
                        </Button>
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(IosSuccessStories);
